import {
  type CodeControle,
  type EtatIaCartes,
  type Modele,
  type MotifEdition,
  RANGS,
  type Rarete,
  type STATUTS_EDITION,
  type TypeEdition,
} from "../contract";

type StatutEdition = (typeof STATUTS_EDITION)[number];

import { detailDe } from "../diffusion-api";

/**
 * Les phrases des ecrans des cartes de la regie (lot 5 du TCG, PR 4) : « Chroniques a valider » et « Editions ».
 * Ecrites pour Nicolas : courtes, au constat ou a l'imperatif. **Rien du serveur n'est affiche tel quel** : un code
 * d'erreur de l'API, un controle, un etat n'ont que leur phrase, ecrite ici, en `Record<Code, texte>` pour chaque liste
 * fermee (un code ajoute au contrat sans sa phrase ne compile pas). Les noms des personnes viennent des donnees et
 * sont rendus comme du texte par les composants.
 */

export const CHRONIQUES_PATH = "/regie/cartes/chroniques";
export const EDITIONS_PATH = "/regie/cartes/editions";
export const SIGNALEMENTS_PATH = "/regie/cartes/signalements";

export const NAV = {
  label: "Cartes",
  regie: "Retour à la régie",
  chroniques: "Chroniques à valider",
  editions: "Éditions",
  boosters: "Boosters",
  signalements: "Signalements",
} as const;

export const CHARGEMENT = "Chargement des cartes...";
export const INDISPONIBLE =
  "L'écran des cartes n'a pas pu être chargé. Recharge la page : Castellan a peut-être été mis à jour depuis l'ouverture de cet onglet.";
export const RECHARGER = "Recharger la page";

export const SESSION = {
  refusee: "Ce compte n'est pas celui de la régie.",
} as const;

/** Le nom d'une rarete, tel que la carte l'ecrit. */
export function nomRarete(rarete: Rarete): string {
  if (rarete === "chatelain") return "Châtelain";
  return RANGS.find((rang) => rang.id === rarete)?.nom ?? rarete;
}

/** Le nom d'un modele de l'IA, en toutes lettres. */
export const NOMS_MODELES: Record<Modele, string> = {
  "claude-opus-5-5": "Claude Opus 5.5",
  "claude-sonnet-5-5": "Claude Sonnet 5.5",
  "claude-haiku-5-5": "Claude Haiku 5.5",
};

/** Des centimes en euros, a la francaise : 42 donne « 0,42 € ». */
export function euros(centimes: number): string {
  return `${(centimes / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

// --- La file des chroniques -------------------------------------------------------------------------

export const FILE = {
  titre: "Chroniques à valider",
  aValider: "À valider",
  chargement: "Lecture de la file...",
  lecture: "La file n'a pas pu être lue. Réessaie dans un instant.",
  relire: "Réessayer",
  filtres: {
    label: "Filtrer la file",
    toutes: "Toutes",
    montees: "Montées de rang",
    raids: "Raids",
  },
  listeLabel: "File d'attente",
  vide: {
    titre: "Rien à valider",
    texte:
      "Toutes les chroniques sont validées. La prochaine file arrive après le stream, ou à la prochaine montée de rang.",
  },
  videFiltre: "Aucune chronique dans ce filtre.",
  attente: (n: number) =>
    n === 0 ? "" : n === 1 ? "Une chronique attend l'IA." : `${n} chroniques attendent l'IA.`,
  cout: { mois: "IA ce mois", sur: "sur" },
  etiquettes: {
    sansIa: "Sans IA",
    relance: "Relance",
    echec: "Échec",
    aEcrire: "À écrire",
    parToi: "À écrire par toi",
  },
} as const;

export const MOTIFS: Record<MotifEdition, string> = {
  premiere: "Nouvelle carte",
  montee: "Montée de rang",
  raid: "Raid",
  moment: "Moment",
  chatelain: "Le Châtelain",
};

/** « Montée de rang : Paladin », « Nouvelle carte »... */
export function motifText(motif: MotifEdition, rarete: Rarete): string {
  return motif === "montee" ? `${MOTIFS.montee} : ${nomRarete(rarete)}` : MOTIFS[motif];
}

/** « 3e édition · saison 1 ». */
export function editionText(type: TypeEdition, numero: number, saison: number): string {
  if (type === "chatelain") return `Hors série · saison ${saison}`;
  return `${numero === 1 ? "1re" : `${numero}e`} édition · saison ${saison}`;
}

export const IA_BANDEAUX: Record<
  Exclude<EtatIaCartes, "ok">,
  { titre: string; texte: string; rouge: boolean }
> = {
  plafond: {
    titre: "Plafond de l'IA atteint",
    texte:
      "Les chroniques attendent. Les cartes continuent de circuler avec leur édition précédente, et les premières cartes restent hors des boosters. Le plafond de l'app se règle dans les réglages de la régie.",
    rouge: true,
  },
  sous_plafond: {
    titre: "Sous-plafond des cartes atteint",
    texte:
      "Les chroniques attendent le mois prochain. Les cartes continuent de circuler avec leur édition précédente. Tu peux relever le sous-plafond dans l'écran Éditions.",
    rouge: true,
  },
  non_configure: {
    titre: "L'IA n'est pas branchée",
    texte:
      "La clé de l'IA n'est pas posée sur le serveur : les chroniques attendent. Tu peux écrire les chroniques toi-même.",
    rouge: true,
  },
  en_pause: {
    titre: "Le Lab a mis l'IA en pause",
    texte: "Les chroniques attendent la fin de la pause. Rien n'est perdu.",
    rouge: false,
  },
};

export const DETAIL = {
  choisir: "Choisis une chronique dans la file.",
  edition: "édition",
  texte: {
    ia: "Chronique écrite par l'IA",
    ia_corrigee: "Chronique écrite par l'IA, corrigée par toi",
    sans_ia: "Ligne sans IA (écrite d'après les chiffres)",
    nicolas: "Chronique écrite par toi",
    aucune: "Chronique à écrire",
    echec: "L'IA n'a pas rendu de texte",
    attente: "L'IA n'a pas encore écrit cette chronique",
    parToi: "À écrire par toi",
  },
  champ: "Texte de la chronique",
  compteur: (n: number, max: number) => `${n} / ${max} caractères`,
  modele: (nom: string) => `Modèle : ${nom}`,
  controles: "Contrôles",
  sources: {
    titre: "Ce que l'IA a lu",
    description: "Description Twitch",
    comptes: "Comptes rendus de stream",
    comptesN: (n: number) => (n === 1 ? "1 compte rendu" : `${n} comptes rendus`),
    moments: "Moments forts",
    rien: "Rien : la ligne est tirée des chiffres, sans appel à l'IA.",
    aucune: "L'IA n'a pas encore lu cette carte.",
    nonLu: "Pas de description.",
  },
  apercu: "Aperçu de la carte",
  apercuAide:
    "Une fois validée, l'édition est imprimée en image et entre dans les boosters. L'édition précédente sort jusque-là.",
  apercuAbsent: "L'aperçu n'est pas disponible pour ce texte.",
  valider: "Valider",
  validerEnCours: "Validation...",
  relancer: (restantes: number) =>
    `Relancer l'IA (${restantes} restante${restantes > 1 ? "s" : ""})`,
  relancerEnCours: "Relance...",
  enregistrer: "Enregistrer ma correction",
  enregistrerEnCours: "Enregistrement...",
  pasDeRelance: "Plus de relance : écris ou corrige le texte toi-même.",
  iaArretee: "L'IA est arrêtée : la relance attend.",
  nonRelancable: "Cette carte n'est jamais écrite par l'IA.",
  rienAEcrire: "L'IA n'a rien à relancer : elle n'a pas encore écrit cette carte.",
  bloque: "Un contrôle bloque : corrige le texte avant de valider.",
  trop: "Le texte dépasse 140 caractères : raccourcis-le pour valider.",
  vide: "Écris un texte pour valider.",
  modifie:
    "Le texte a changé : « Valider » l'enregistre d'abord, puis le valide si aucun contrôle ne bloque.",
} as const;

export const CONTROLES: Record<CodeControle, string> = {
  longueur: "140 caractères au plus",
  tirets: "Aucun tiret long ni moyen",
  adresse: "Aucune adresse web",
  arobase: "Aucune arobase",
  emoji: "Aucun émoji (les polices de la carte n'en ont pas)",
  citation: "Pas de citation de plus de quelques mots",
  vie_privee: "Rien sur la vie privée",
  moquerie: "Aucune moquerie sur le physique ou l'identité",
};

export const ISSUES: Record<"ok" | "a_verifier" | "bloquant", string> = {
  ok: "ok",
  a_verifier: "à vérifier",
  bloquant: "bloquant",
};

export const CONTROLES_VIDES = "Les contrôles se calculent quand le texte est enregistré.";

export const ECHECS: Record<"refus" | "invalide" | "indisponible", string> = {
  refus: "L'IA a refusé d'écrire cette chronique.",
  invalide: "L'IA a rendu un texte que les contrôles refusent.",
  indisponible: "L'IA n'a pas répondu.",
};

export const FAIT = {
  corrige: "Correction enregistrée. Les contrôles sont à jour.",
  valide: "Chronique validée : l'image part à l'impression.",
  relance: "L'IA réécrira cette chronique au prochain passage, hors du live.",
} as const;

/** La phrase d'un refus de l'API des cartes : son code, ou son statut, jamais son message. */
export function erreurText(erreur: unknown): string {
  const { code, status } = detailDe(erreur);
  switch (code) {
    case "delai_depasse":
    case "coupure":
    case "contract_broken":
      return "La demande a peut-être abouti : l'écran est relu pour le savoir.";
    case "perime":
      return "Cette édition a changé depuis que tu l'as ouverte : elle est relue.";
    case "bloque":
      return "Un contrôle bloque cette chronique : elle n'est pas validée.";
    case "vide":
      return "Le texte est vide.";
    case "deja_validee":
      return "Cette chronique est déjà validée avec un autre texte.";
    case "relances_epuisees":
      return "Les trois relances de cette édition sont épuisées.";
    case "non_relancable":
      return "Cette carte n'est jamais écrite par l'IA.";
    case "deja_a_ecrire":
      return "L'IA n'a pas encore écrit cette chronique.";
    case "non_validee":
      return "La chronique n'est pas encore validée : rien à réimprimer.";
    case "remplacee":
      return "Cette édition a été remplacée par une plus récente.";
    case "introuvable":
      return "Cette édition n'existe plus : l'écran est relu.";
    case "non_retirable":
      return "Cette carte n'est celle d'aucun viewer : il n'y a rien à retirer.";
    case "deja_traite":
      return "Ce signalement est déjà traité : l'écran est relu.";
    case "corps_trop_lourd":
      return "Ce texte est trop long pour être envoyé.";
    case "base_indisponible":
      return "La base n'a pas répondu. Réessaie dans un instant.";
    default:
      break;
  }
  if (status === 404) return "Cette édition n'existe plus : l'écran est relu.";
  if (status === 429) return "Trop de demandes d'un coup. Réessaie dans un instant.";
  return "La demande a échoué. Réessaie dans un instant.";
}

// --- L'ecran des editions ---------------------------------------------------------------------------

export const EDITIONS = {
  titre: "Éditions",
  saison: (numero: number) => `Saison ${numero}`,
  chargement: "Lecture des éditions...",
  lecture: "Les éditions n'ont pas pu être lues. Réessaie dans un instant.",
  relire: "Réessayer",
  filtres: {
    label: "Filtrer les éditions",
    toutes: "Toutes",
    normale: "Normales",
    raid: "Raids",
    moment: "Moments",
    chatelain: "Châtelain",
  },
  totaux: {
    label: "Totaux de la saison",
    cartes: "cartes",
    editions: "éditions",
    exemplaires: "exemplaires tirés",
    aValider: "chroniques à valider",
    aImprimer: "images à imprimer",
  },
  vide: "Aucune édition pour l'instant. Les cartes naissent au premier stream compté d'un viewer.",
  videFiltre: "Aucune édition dans ce filtre.",
  tableau: "Éditions de la saison",
  colonnes: {
    personne: "Personne",
    rarete: "Rareté",
    edition: "Éd.",
    imprimee: "Imprimée",
    stats: "Stats figées",
    exemplaires: "Ex.",
    chronique: "Chronique",
    etat: "État",
  },
  nonImprimee: "Pas encore",
  libre: "libre",
  ouvrir: (nom: string, numero: number) => `Voir l'édition ${numero} de ${nom}`,
  apercu: {
    titre: "Édition sélectionnée",
    choisir: "Choisis une édition dans le tableau pour voir son image.",
    image: (nom: string) => `Image imprimée de la carte de ${nom}`,
    pasImprimee: "L'image n'est pas encore imprimée.",
    imageEchec: "L'impression de cette image a échoué trois fois.",
    imageEnAttente: "L'image attend son tour d'impression.",
    imprimee: "Image",
    tirage: "Tirage",
    reimprimer: "Réimprimer l'image",
    reimprimerEnCours: "Demande...",
    reimprimeeOk: "L'image est remise à imprimer.",
    retirer: "Retirer la carte",
    retirerTitre: (nom: string) => `Retirer la carte de ${nom} ?`,
    retirerTexte:
      "C'est définitif : toutes ses éditions et tous leurs exemplaires disparaissent, chez tous les collectionneurs. Ce que la personne possède des autres reste à elle. La carte ne sera jamais recréée.",
    retirerConfirmer: "Retirer définitivement",
    retirerGarder: "Garder la carte",
    retiree: "La carte est retirée.",
  },
  chroniqueEtats: {
    a_ecrire: "À écrire",
    a_valider: "À valider",
    echec: "Échec",
    validee: "Validée",
  },
  sourcesTags: { sans_ia: "Sans IA" },
  relancee: "Relancée",
  statuts: {
    en_circulation: "En circulation",
    en_attente: "En attente",
    archivee: "Archivée",
    remplacee: "Remplacée",
  } satisfies Record<StatutEdition, string>,
  imageAReimprimer: "À imprimer",
} as const;

export const REGLAGES_IA = {
  titre: "IA des cartes",
  aide: "Le modèle qui écrit les chroniques, et ce que les cartes peuvent dépenser par mois. Le plafond de l'app, que tient le Lab, reste au-dessus.",
  modele: "Modèle",
  plafond: "Sous-plafond du mois (en euros)",
  enregistrer: "Enregistrer",
  enregistrerEnCours: "Enregistrement...",
  enregistre: "Réglages enregistrés.",
  chargement: "Lecture des réglages...",
  lecture: "Les réglages n'ont pas pu être lus.",
  plafondInvalide: "Le sous-plafond va de 0 à 50 euros.",
} as const;

// --- L'ecran des signalements -----------------------------------------------------------------------

export const SIGNALEMENTS = {
  titre: "Cartes signalées",
  chargement: "Lecture des signalements...",
  lecture: "Les signalements n'ont pas pu être lus. Réessaie dans un instant.",
  relire: "Réessayer",
  aide: "N'importe qui peut signaler une carte depuis sa page publique, sans que tu saches qui. Retirer une carte est définitif.",
  ouverts: "À traiter",
  traites: "Déjà traités",
  vide: {
    titre: "Rien à traiter",
    texte: "Aucune carte n'est signalée en ce moment.",
  },
  videTraites: "Aucun signalement traité pour l'instant.",
  image: (nom: string) => `Image imprimée de la carte de ${nom}`,
  pasImprimee: "L'image n'est pas encore imprimée.",
  motifs: {
    blessante: "La chronique est blessante",
    privee: "Elle dévoile quelque chose de privé",
    autre: "Autre chose",
  },
  fois: (n: number) => (n === 1 ? "1 fois" : `${n} fois`),
  premier: (jour: string) => `Premier signalement : ${jour}`,
  dernier: (jour: string) => `Dernier : ${jour}`,
  voir: "Voir la page publique",
  retirer: "Retirer la carte",
  garder: "Garder la carte",
  enCours: "Traitement...",
  nonRetirable: "Cette carte n'est celle d'aucun viewer : il n'y a rien à retirer.",
  retirerTitre: (nom: string) => `Retirer la carte de ${nom} ?`,
  retirerTexte:
    "C'est définitif : toutes ses éditions et tous leurs exemplaires disparaissent, chez tous les collectionneurs. Ce que la personne possède des autres reste à elle. La carte ne sera jamais recréée.",
  retirerConfirmer: "Retirer définitivement",
  retirerAnnuler: "Garder la carte",
  retiree: "La carte est retirée.",
  gardee: "Le signalement est fermé, la carte reste.",
  decisions: { gardee: "Gardée", retiree: "Retirée" },
  le: (jour: string) => `le ${jour}`,
} as const;
